/**
 * Mycelium domain event validation.
 *
 * Provides a discriminated union (`MyceliumDomainEvent`) of all typed
 * domain events, plus a `validateEventPayload()` helper for use at
 * worker boundaries.
 *
 * **Schema evolution rule:** All payload fields added after initial
 * release MUST be `.optional()` — the Zod `.extend()` pattern
 * enforces Kafka-style backward compatibility automatically.
 *
 * @module validation
 */

import { z, type ZodIssue } from 'zod'
import { PGMQMessageSchema, type PGMQMessage } from '../types/backbone.ts'
import type { BackboneEvent } from '../types/backbone.ts'

// ── Import all domain events ────────────────────────────────────────────

import { TaskDomainEvents } from './task.events.ts'
import { GamificationDomainEvents } from './gamification.events.ts'
import { SalesDomainEvents } from './sales.events.ts'
import { AccountingDomainEvents } from './accounting.events.ts'
import { NotificationDomainEvents } from './notification.events.ts'
import { IntegrationDomainEvents } from './integration.events.ts'
import { AiDomainEvents } from './ai.events.ts'
import { SystemDomainEvents } from './system.events.ts'
import { CalendarDomainEvents } from './calendar.events.ts'
import { WebhookDomainEvents } from './webhook.events.ts'
import { CortexDomainEvents } from './cortex.events.ts'
import { WorkloadDomainEvents } from './workload.events.ts'
import { RowChangeDomainEvents } from './row-change.events.ts'
import { FormsDomainEvents } from './forms.events.ts'
import { NotebookDomainEvents } from './notebook.events.ts'
import { MemoryDomainEvents } from './memory.events.ts'
import { ResearchDomainEvents } from './research.events.ts'
import { BoxDomainEvents } from './box.events.ts'
import { MessagingDomainEvents } from './messaging.events.ts'
import { A2aDomainEvents } from './a2a.events.ts'
import { OntologyDomainEvents } from './ontology.events.ts'
import { DocumentsDomainEvents } from './documents.events.ts'

// ── Collect all typed event schemas ─────────────────────────────────────

const allDomainEventSchemas = [
  ...TaskDomainEvents,
  ...GamificationDomainEvents,
  ...SalesDomainEvents,
  ...AccountingDomainEvents,
  ...NotificationDomainEvents,
  ...IntegrationDomainEvents,
  ...AiDomainEvents,
  ...SystemDomainEvents,
  ...CalendarDomainEvents,
  ...WebhookDomainEvents,
  ...CortexDomainEvents,
  ...WorkloadDomainEvents,
  ...RowChangeDomainEvents,
  ...FormsDomainEvents,
  ...NotebookDomainEvents,
  ...MemoryDomainEvents,
  ...ResearchDomainEvents,
  ...BoxDomainEvents,
  ...MessagingDomainEvents,
  ...A2aDomainEvents,
  ...OntologyDomainEvents,
  ...DocumentsDomainEvents,
] as const

// ── Build a lookup map: event type → schema ─────────────────────────────

type EventTypeToSchema = Map<string, z.ZodTypeAny>

function buildSchemaMap(): EventTypeToSchema {
  const map: EventTypeToSchema = new Map()
  for (const schema of allDomainEventSchemas) {
    // Each schema is a BackboneEventSchema.extend() with type: z.literal(...)
    // Extract the literal value from the shape's type field.
    const shape = (schema as z.ZodObject<z.ZodRawShape>).shape
    if (shape && 'type' in shape) {
      const typeField = shape.type
      // z.ZodLiteral stores the value in ._def.value
      if (typeField && '_def' in typeField && 'value' in (typeField as z.ZodLiteral<string>)._def) {
        map.set((typeField as z.ZodLiteral<string>)._def.value, schema)
      }
    }
  }
  return map
}

const eventSchemaMap = buildSchemaMap()

// ── Discriminated union ─────────────────────────────────────────────────

/**
 * Discriminated union of ALL typed Mycelium domain events.
 *
 * Use `.safeParse()` to validate an event with full payload type checking.
 * Unknown event types (not yet in the union) will fail validation —
 * use `validateEventPayload()` for graceful handling of unknown types.
 */
export const MyceliumDomainEvent = z.union(
  allDomainEventSchemas as unknown as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]
)
export type MyceliumDomainEvent = z.infer<typeof MyceliumDomainEvent>

// ── DLQ reason constant ─────────────────────────────────────────────────

export const SCHEMA_VALIDATION_FAILED = 'SCHEMA_VALIDATION_FAILED' as const

// ── Validation result ───────────────────────────────────────────────────

export type EventValidationResult =
  | { valid: true; knownType: true; data: MyceliumDomainEvent }
  | { valid: true; knownType: false }
  | { valid: false; knownType: true; error: z.ZodError; reason: typeof SCHEMA_VALIDATION_FAILED }

/**
 * Validate an event's payload against the typed domain schema.
 *
 * Behaviour:
 * - **Known type, valid payload** → `{ valid: true, knownType: true, data }`
 * - **Unknown type** → `{ valid: true, knownType: false }` (pass-through)
 * - **Known type, invalid payload** → `{ valid: false, knownType: true, error, reason }`
 *
 * Unknown event types are allowed through because producers may publish
 * new event types before the schema registry is updated. Only events
 * with a known type but invalid payload are rejected.
 */
export function validateEventPayload(
  event: { type: string; domain?: string; payload?: unknown } & Record<string, unknown>
): EventValidationResult {
  const schema = eventSchemaMap.get(event.type)

  if (!schema) {
    // Unknown event type — pass through without validation
    return { valid: true, knownType: false }
  }

  const result = schema.safeParse(event)

  if (result.success) {
    return { valid: true, knownType: true, data: result.data as MyceliumDomainEvent }
  }

  return {
    valid: false,
    knownType: true,
    error: result.error,
    reason: SCHEMA_VALIDATION_FAILED,
  }
}

/**
 * Get the set of all registered (known) event type strings.
 * Useful for metrics and debugging.
 */
export function getRegisteredEventTypes(): ReadonlySet<string> {
  return new Set(eventSchemaMap.keys())
}

/**
 * Check if an event type has a registered schema.
 */
export function isKnownEventType(type: string): boolean {
  return eventSchemaMap.has(type)
}

/** Minimal queue message shape (PGMQ / mycelium-worker). */
export interface QueueEventLike {
  type: string
  domain?: string
  payload?: unknown
  userId?: string
  orgId?: string
  principalId?: string
}

/**
 * Validate a pre-publish event payload against registered domain schemas.
 *
 * Builds a synthetic {@link BackboneEvent} envelope with valid metadata so
 * the domain-specific Zod schemas (which extend `BackboneEventSchema`) can
 * validate the payload without requiring the caller to construct a full
 * BackboneEvent.
 *
 * **Pre-publish validation** — called by `publishMyceliumEvent()` before
 * `buildPGMQMessage()` transforms the envelope into a PGMQ message.
 */
export function validateQueuePayload(
  event: QueueEventLike
): { valid: true } | { valid: false; error: string } {
  const schema = eventSchemaMap.get(event.type)
  if (!schema) {
    return { valid: true }
  }

  const candidate = {
    id: 'queue-validation',
    timestamp: Date.now(),
    domain: event.domain ?? 'system',
    type: event.type,
    principalId: event.principalId ?? event.userId,
    userId: event.userId,
    orgId: event.orgId,
    payload: (event.payload ?? {}) as Record<string, unknown>,
    metadata: {
      source: 'queue-validation',
      priority: 5,
      processedBy: [],
      context: {},
      tags: [],
    },
    version: 1 as const,
  }

  const result = schema.safeParse(candidate)
  if (result.success) {
    return { valid: true }
  }

  const issues = result.error.issues
    .map((issue: ZodIssue) => `${issue.path.join('.')}: ${issue.message}`)
    .join('; ')
  return { valid: false, error: `${SCHEMA_VALIDATION_FAILED}: ${issues}` }
}

/**
 * Validate a PGMQ message (BackboneEvent) against the canonical
 * `PGMQMessageSchema` AND the registered domain event schema.
 *
 * **Post-build / consume-time validation** — called by the worker after
 * reading from PGMQ, or by publishers after `buildPGMQMessage()`.
 *
 * @since 2026-07-04
 */
export function validatePGMQMessage(
  message: unknown
): { valid: true; data: PGMQMessage } | { valid: false; error: string } {
  // First validate against the canonical BackboneEvent shape
  const backboneResult = PGMQMessageSchema.safeParse(message)
  if (!backboneResult.success) {
    const issues = backboneResult.error.issues
      .map((issue: ZodIssue) => `${issue.path.join('.')}: ${issue.message}`)
      .join('; ')
    return { valid: false, error: `${SCHEMA_VALIDATION_FAILED}: ${issues}` }
  }

  // Then validate against the domain-specific schema (if registered)
  const msg = backboneResult.data as PGMQMessage
  const domainSchema = eventSchemaMap.get(msg.type)
  if (!domainSchema) {
    // Unknown event type — pass through (same policy as validateQueuePayload)
    return { valid: true, data: msg }
  }

  const domainResult = domainSchema.safeParse(msg)
  if (domainResult.success) {
    return { valid: true, data: msg }
  }

  const issues = domainResult.error.issues
    .map((issue: ZodIssue) => `${issue.path.join('.')}: ${issue.message}`)
    .join('; ')
  return { valid: false, error: `${SCHEMA_VALIDATION_FAILED}: ${issues}` }
}
